import {Component, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {ActivatedRoute, Router} from '@angular/router';
import {forkJoin, Observable, of, switchMap} from 'rxjs';
import {AppRoutes} from '../../../../enums/routes';
import {SignalRHttpService} from '../../x-http-requests/signal-r-http.service';
import {GlobalAlertService} from '../../../../_services/global-alert.service';
import {PermissionService} from '../../../../_auth/permission.service';
import {AlertLevel} from '../../../../enums/alert-level';
import {Device} from '../../x-models/device';
import {ConfirmPopupComponent} from '../../../../_components/confirm-popup/confirm-popup.component';
import {DeviceLifecycleHttpService} from '../../../device-lifecycle/data-access/device-lifecycle-http.service';
import {
  DecommissionReasonOption,
  DeviceLifecycle
} from '../../../device-lifecycle/shared/models/device-lifecycle.model';
import {DayKey, isDayKey, parseDayKey} from '../../../../_shared/time/company-day';

/**
 * Per-device management page (reached from the device overview's edit action, in the Standort/shard context).
 * It edits the device's friendly **name** and **lock** state, manages its **fiscal lifecycle** (acquisition date,
 * commissioning — read-only — and decommissioning), and deletes the device. Deleting is **unified with
 * decommissioning**: the trash action requires a structured reason and, for a registered device, records the
 * decommission on its DeviceService fiscal client before removing the monolith device.
 *
 * The fiscal lifecycle is keyed by the device's {@link Device.clientId} (the KassenSichV registration ExternalId,
 * surfaced on the monolith device DTO); a device that has never registered has no client id, so the lifecycle card
 * and the decommission reason are omitted and a plain delete is offered.
 *
 * The lifecycle dates are calendar days. DeviceService sends them as plain `yyyy-MM-dd`, the page shows them and
 * fills the date field with them as they are, and posts the field's `yyyy-MM-dd` back. No time zone is involved, so
 * the page shows the day the tax office is sent whatever zone the admin's browser is in.
 */
@Component({
  selector: 'app-device-settings-page',
  standalone: true,
  imports: [FormsModule, NgSelectModule, ConfirmPopupComponent],
  template: `
    <div class="px-4 sm:px-6 lg:px-8 py-4 absolute inset-0 overflow-y-auto">
      <div class="mx-auto w-full max-w-3xl flex flex-col gap-4">

        <!-- Header -->
        <div class="flex items-center gap-3">
          <a [href]="'/' + overviewRoute" aria-label="Zurück zur Geräteübersicht"
             class="shrink-0 text-gray-500 hover:text-gray-900 transition-colors">
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"/>
            </svg>
          </a>
          <div>
            <h1 class="text-xl font-bold tracking-tight text-gray-900">Gerät</h1>
            <p class="mt-0.5 text-sm text-gray-500 truncate">{{ device?.name }}</p>
          </div>
        </div>

        @if (loading) {
          <div class="flex items-center justify-center py-20" aria-label="Lädt">
            <svg class="h-8 w-8 animate-spin text-theme-primary-500" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" d="M4 12a8 8 0 018-8" fill="currentColor"></path>
            </svg>
          </div>
        } @else if (error) {
          <div class="p-4 bg-red-50 border border-red-100 rounded-2xl shadow-sm">
            <p class="text-sm font-semibold text-red-600">Das Gerät konnte nicht geladen werden.</p>
            <button (click)="reload()" class="mt-2 inline-flex items-center text-red-600 bg-red-100 hover:bg-red-200 rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200">Erneut versuchen</button>
          </div>
        } @else if (device) {

          <!-- Identity / hardware (read-only) -->
          <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <p class="text-base font-bold text-gray-800 tracking-tight mb-3">Gerät</p>
            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div class="flex justify-between gap-3 border-b border-gray-50 py-1 sm:border-0">
                <dt class="text-gray-500">Geräte-ID</dt>
                <dd class="text-gray-900 font-medium truncate text-right">{{ device.id }}</dd>
              </div>
              <div class="flex justify-between gap-3 border-b border-gray-50 py-1 sm:border-0">
                <dt class="text-gray-500">Seriennummer</dt>
                <dd class="text-gray-900 font-medium truncate text-right">{{ device.serialNumber || '—' }}</dd>
              </div>
              <div class="flex justify-between gap-3 border-b border-gray-50 py-1 sm:border-0">
                <dt class="text-gray-500">Hardware</dt>
                <dd class="text-gray-900 font-medium truncate text-right">{{ hardware() }}</dd>
              </div>
              <div class="flex justify-between gap-3 border-b border-gray-50 py-1 sm:border-0">
                <dt class="text-gray-500">Betriebssystem</dt>
                <dd class="text-gray-900 font-medium truncate text-right">{{ os() }}</dd>
              </div>
              <div class="flex justify-between gap-3 border-b border-gray-50 py-1 sm:border-0">
                <dt class="text-gray-500">App-Version</dt>
                <dd class="text-gray-900 font-medium truncate text-right">{{ appVersion() }}</dd>
              </div>
              <div class="flex justify-between gap-3 py-1">
                <dt class="text-gray-500">Verkaufspunkt</dt>
                <dd class="text-gray-900 font-medium truncate text-right">{{ device.location?.name || '—' }}</dd>
              </div>
            </dl>
          </div>

          <!-- Editable settings: name + lock -->
          <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-4">
            <p class="text-base font-bold text-gray-800 tracking-tight">Einstellungen</p>

            @if (canChangeName) {
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="name">Name</label>
                <input type="text" id="name" name="name" [(ngModel)]="name"
                       class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"/>
              </div>
            }

            @if (canSetLockedOut) {
              <div class="flex items-center justify-between gap-3">
                <span class="text-sm font-bold text-gray-700 tracking-tight">Gesperrt</span>
                <div class="inline-flex rounded-xl border border-gray-200 bg-white p-1 shadow-sm shrink-0" role="group" aria-label="Gesperrt">
                  @for (opt of lockOptions; track opt.value) {
                    <button type="button" (click)="lockedOut = opt.value" [attr.aria-pressed]="lockedOut === opt.value"
                            class="px-3 py-1 text-sm font-medium rounded-lg transition-all duration-200"
                            [class.bg-theme-primary-50]="lockedOut === opt.value"
                            [class.text-theme-primary-700]="lockedOut === opt.value"
                            [class.shadow-sm]="lockedOut === opt.value"
                            [class.text-gray-500]="lockedOut !== opt.value">{{ opt.label }}</button>
                  }
                </div>
              </div>
            }

            @if (canChangeName || canSetLockedOut) {
              <div class="flex justify-end">
                <button type="button" (click)="saveSettings()" [disabled]="saving || !settingsChanged()"
                        class="inline-flex items-center justify-center px-5 py-2.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">
                  Speichern
                </button>
              </div>
            }
          </div>

          <!-- Fiscal lifecycle -->
          <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-4">
            <p class="text-base font-bold text-gray-800 tracking-tight">Fiskalischer Lebenszyklus</p>

            @if (!device.clientId) {
              <p class="text-sm text-gray-500 italic">
                Dieses Gerät ist noch nicht fiskalisch registriert — der Lebenszyklus erscheint nach der ersten Anmeldung.
              </p>
            } @else if (lifecycle) {
              @if (lifecycle.inService) {
                <div class="inline-flex items-center gap-2 self-start rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
                  <span class="h-2 w-2 rounded-full bg-green-500"></span> In Betrieb
                </div>
              } @else {
                <div class="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm">
                  <p class="font-semibold text-amber-800">Außer Betrieb seit {{ fmt(lifecycle.decommissionedOn) }}</p>
                  <p class="text-amber-700">Grund: {{ reasonLabel(lifecycle.decommissionReason) }}</p>
                  @if (lifecycle.decommissionNote) {
                    <p class="text-amber-700">Notiz: {{ lifecycle.decommissionNote }}</p>
                  }
                  <p class="mt-1 text-xs text-amber-700">
                    Wird bei erneuter Anmeldung automatisch wieder in Betrieb genommen (Anschaffungsdatum bleibt erhalten).
                  </p>
                </div>
              }

              <!-- Troubleshooting: the last push to the fiscal provider failed (e.g. during a TSS rotation). -->
              @if (lifecycle.lastProviderSyncError) {
                <div class="rounded-xl bg-red-50 border border-red-100 p-3 text-sm flex flex-col gap-2">
                  <p class="font-semibold text-red-700">Fiskal-Synchronisation fehlgeschlagen</p>
                  <p class="text-red-700">
                    Die Änderung wurde gespeichert, aber das Gerät konnte nicht mit dem Fiskal-Anbieter synchronisiert
                    werden. Das kann während einer TSS-Rotation auftreten — das Gerät synchronisiert sich bei seiner
                    nächsten Anmeldung automatisch neu (ggf. mit einer neuen Fiskal-ID). Sie können es auch jetzt erneut
                    versuchen.
                  </p>
                  <p class="text-xs text-red-500 break-words font-mono">{{ lifecycle.lastProviderSyncError }}</p>
                  <div>
                    <button type="button" (click)="resync()" [disabled]="saving"
                            class="inline-flex items-center justify-center px-3 py-1.5 text-red-700 bg-red-100 hover:bg-red-200 rounded-lg text-sm font-medium transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed">
                      Erneut synchronisieren
                    </button>
                  </div>
                </div>
              }

              <div class="flex flex-col sm:flex-row sm:items-end gap-3">
                <div class="grow">
                  <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="acquired">Anschaffungsdatum</label>
                  <input type="date" id="acquired" name="acquired" [(ngModel)]="acquiredOnInput"
                         class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"/>
                </div>
                <button type="button" (click)="saveAcquiredOn()" [disabled]="saving || !acquiredOnInput || !acquiredChanged()"
                        class="inline-flex items-center justify-center px-5 py-2.5 bg-theme-primary-600 text-white rounded-2xl hover:bg-theme-primary-700 font-semibold shadow-lg shadow-theme-primary-100 hover:shadow-xl transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:grayscale disabled:cursor-not-allowed">
                  Speichern
                </button>
              </div>

              <div>
                <p class="text-sm font-bold text-gray-700 tracking-tight">Inbetriebnahme</p>
                <p class="text-sm text-gray-600">{{ fmt(lifecycle.commissionedOn) }}</p>
              </div>
            }
          </div>

          <!-- Delete (= decommission) -->
          @if (canDelete) {
            <div class="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between gap-3">
              <div class="min-w-0">
                <p class="text-base font-bold text-gray-800 tracking-tight">Gerät löschen</p>
                <p class="text-xs text-gray-500">
                  {{ device.clientId ? 'Nimmt das Gerät mit Begründung außer Betrieb und entfernt es.' : 'Entfernt das Gerät.' }}
                </p>
              </div>
              <button type="button" (click)="openDelete()" [disabled]="saving"
                      class="shrink-0 inline-flex items-center justify-center px-4 py-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-2xl font-semibold transition-all duration-200 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed">
                Löschen
              </button>
            </div>
          }
        }
      </div>
    </div>

    @if (showDeleteDialog && device) {
      <layout-confirm-popup [title]="device.clientId ? 'Gerät außer Betrieb nehmen & löschen' : 'Gerät löschen'"
                            confirmText="Löschen" cancelText="Abbrechen"
                            (onCancel)="cancelDelete()" (onSubmit)="confirmDelete()">
        <div class="flex flex-col gap-3 py-2">
          @if (device.clientId) {
            <p class="text-sm text-gray-600">Das Gerät wird mit dem gewählten Grund außer Betrieb genommen und anschließend gelöscht.</p>
            <div>
              <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="reason">Grund</label>
              <ng-select labelForId="reason" name="reason" class="ng-select cheqme-style text-sm" [clearable]="false"
                         placeholder="Bitte wählen…" [(ngModel)]="selectedReason">
                @for (r of reasons; track r.reason) {
                  <ng-option [value]="r.reason">{{ r.label }}</ng-option>
                }
              </ng-select>
            </div>
            @if (selectedReasonRequiresNote) {
              <div>
                <label class="block text-sm font-bold text-gray-700 mb-2 tracking-tight" for="note">Notiz</label>
                <textarea id="note" name="note" rows="2" [(ngModel)]="decommissionNote" placeholder="Bitte beschreiben…"
                          class="block w-full px-3 py-2 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-theme-primary-500 focus:ring-1 focus:ring-theme-primary-500 sm:text-sm"></textarea>
              </div>
            }
          } @else {
            <p class="text-sm text-gray-600">Dieses Gerät wird endgültig entfernt.</p>
          }
          @if (dialogError) {
            <p class="text-sm font-medium text-red-600">{{ dialogError }}</p>
          }
        </div>
      </layout-confirm-popup>
    }
  `
})
export class DeviceSettingsPageComponent implements OnInit {
  protected readonly overviewRoute = AppRoutes.DeviceOverview;
  protected readonly lockOptions: { value: boolean; label: string }[] = [
    {value: false, label: 'Nein'},
    {value: true, label: 'Ja'}
  ];

  protected loading = true;
  protected error = false;
  protected saving = false;

  device?: Device;
  lifecycle: DeviceLifecycle | null = null;
  protected reasons: DecommissionReasonOption[] = [];

  // editable settings
  name = '';
  lockedOut = false;
  acquiredOnInput = '';

  // delete/decommission dialog
  showDeleteDialog = false;
  selectedReason?: number;
  decommissionNote = '';
  protected dialogError = '';

  private id = '';
  protected canChangeName = false;
  protected canSetLockedOut = false;
  protected canDelete = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private signalRHttpService: SignalRHttpService,
    private lifecycleHttp: DeviceLifecycleHttpService,
    private alert: GlobalAlertService,
    permissionService: PermissionService
  ) {
    this.canChangeName = permissionService.CheckPermission(permissionService.Device_Information_ChangeName());
    this.canSetLockedOut = permissionService.CheckPermission(permissionService.Device_Information_SetLockedOut());
    this.canDelete = permissionService.CheckPermission(permissionService.Device_Information_Delete());
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.queryParams['id'] ?? '';
    this.reload();
  }

  reload(): void {
    if (!this.id) {
      this.error = true;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = false;
    this.signalRHttpService.get(this.id).subscribe({
      next: device => {
        this.device = device;
        this.name = device.name ?? '';
        this.lockedOut = device.isLockedOut;
        if (device.clientId) this.loadLifecycle(device.clientId);
        else this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      }
    });
  }

  private loadLifecycle(clientId: string): void {
    forkJoin({lifecycle: this.lifecycleHttp.get(clientId), reasons: this.lifecycleHttp.reasons()}).subscribe({
      next: ({lifecycle, reasons}) => {
        this.lifecycle = lifecycle;
        this.reasons = reasons;
        this.acquiredOnInput = this.toDateInput(lifecycle.acquiredOn);
        this.loading = false;
      },
      // A registered device with no fiscal client yet (404) is not an error — show "not registered".
      error: () => {
        this.lifecycle = null;
        this.loading = false;
      }
    });
  }

  // ---- settings (name + lock) --------------------------------------------------------------------------

  settingsChanged(): boolean {
    return (this.canChangeName && this.name !== (this.device?.name ?? '')) ||
      (this.canSetLockedOut && this.lockedOut !== (this.device?.isLockedOut ?? false));
  }

  saveSettings(): void {
    if (!this.device || this.saving || !this.settingsChanged()) return;
    const id = this.device.id;
    const calls: Observable<unknown>[] = [];
    if (this.canChangeName && this.name !== this.device.name) calls.push(this.signalRHttpService.changeName(id, this.name));
    if (this.canSetLockedOut && this.lockedOut !== this.device.isLockedOut) calls.push(this.signalRHttpService.setLockedOut(id, this.lockedOut));
    if (calls.length === 0) return;

    this.saving = true;
    forkJoin(calls).subscribe({
      next: () => {
        this.saving = false;
        if (this.device) {
          this.device.name = this.name;
          this.device.isLockedOut = this.lockedOut;
        }
        this.alert.createAndShow('Gespeichert', 'Die Einstellungen wurden gespeichert.', AlertLevel.success, 2000);
      },
      error: () => {
        this.saving = false;
        this.alert.createAndShow('Fehler', 'Die Einstellungen konnten nicht gespeichert werden.', AlertLevel.error, 3000);
      }
    });
  }

  // ---- lifecycle: acquisition date ---------------------------------------------------------------------

  acquiredChanged(): boolean {
    return !!this.lifecycle && this.acquiredOnInput !== this.toDateInput(this.lifecycle.acquiredOn);
  }

  saveAcquiredOn(): void {
    if (!this.device?.clientId || !this.acquiredOnInput || this.saving) return;
    this.saving = true;
    this.lifecycleHttp.setAcquiredOn(this.device.clientId, this.acquiredOnInput).subscribe({
      next: lifecycle => {
        this.saving = false;
        this.lifecycle = lifecycle;
        this.acquiredOnInput = this.toDateInput(lifecycle.acquiredOn);
        this.alert.createAndShow('Gespeichert', 'Das Anschaffungsdatum wurde gespeichert.', AlertLevel.success, 2000);
      },
      error: err => {
        this.saving = false;
        this.alert.createAndShow('Fehler',
          err?.status === 403 ? 'Keine Berechtigung für diese Aktion.' : 'Das Anschaffungsdatum konnte nicht gespeichert werden.',
          AlertLevel.error, 3000);
      }
    });
  }

  /** Re-attempt the fiscal-provider sync (troubleshooting action when a prior push failed). */
  resync(): void {
    if (!this.device?.clientId || this.saving) return;
    this.saving = true;
    this.lifecycleHttp.resync(this.device.clientId).subscribe({
      next: lifecycle => {
        this.saving = false;
        this.lifecycle = lifecycle;
        if (lifecycle.lastProviderSyncError)
          this.alert.createAndShow('Weiterhin nicht synchron',
            'Die Synchronisation ist erneut fehlgeschlagen — sie erfolgt automatisch bei der nächsten Anmeldung.',
            AlertLevel.warning, 3000);
        else
          this.alert.createAndShow('Synchronisiert', 'Das Gerät wurde mit dem Fiskal-Anbieter synchronisiert.',
            AlertLevel.success, 2000);
      },
      error: () => {
        this.saving = false;
        this.alert.createAndShow('Fehler', 'Die Synchronisation konnte nicht ausgeführt werden.', AlertLevel.error, 3000);
      }
    });
  }

  // ---- delete (= decommission) -------------------------------------------------------------------------

  openDelete(): void {
    this.selectedReason = this.reasons[0]?.reason;
    this.decommissionNote = '';
    this.dialogError = '';
    this.showDeleteDialog = true;
  }

  cancelDelete(): void {
    this.showDeleteDialog = false;
  }

  get selectedReasonRequiresNote(): boolean {
    return this.reasons.find(r => r.reason === this.selectedReason)?.requiresNote ?? false;
  }

  /**
   * Confirm deletion. For a registered device this first records the decommission (date + structured reason) on
   * its DeviceService fiscal client, then deletes the monolith device — one unified action. An unregistered device
   * (no client id) is simply deleted.
   */
  confirmDelete(): void {
    if (!this.device || this.saving) return;
    const clientId = this.device.clientId;

    if (clientId) {
      if (this.selectedReason === undefined) {
        this.dialogError = 'Bitte einen Grund auswählen.';
        return;
      }
      if (this.selectedReasonRequiresNote && !this.decommissionNote.trim()) {
        this.dialogError = 'Für diesen Grund ist eine Notiz erforderlich.';
        return;
      }
    }

    const id = this.device.id;
    this.saving = true;
    const decommission$: Observable<unknown> = clientId && this.selectedReason !== undefined
      ? this.lifecycleHttp.decommission(clientId, this.selectedReason, this.selectedReasonRequiresNote ? this.decommissionNote.trim() : null)
      : of(null);

    decommission$.pipe(switchMap(() => this.signalRHttpService.delete(id))).subscribe({
      next: () => {
        this.saving = false;
        this.showDeleteDialog = false;
        this.alert.createAndShow('Gerät gelöscht', 'Das Gerät wurde erfolgreich gelöscht.', AlertLevel.success, 3000);
        this.router.navigate([AppRoutes.DeviceOverview]);
      },
      error: () => {
        this.saving = false;
        this.dialogError = 'Das Gerät konnte nicht gelöscht werden.';
      }
    });
  }

  // ---- display helpers ---------------------------------------------------------------------------------

  hardware(): string {
    const i = this.device?.deviceInformation;
    return [i?.manufacturer, i?.model].filter(Boolean).join(' ') || '—';
  }

  os(): string {
    const i = this.device?.deviceInformation;
    return [i?.platform, i?.osVersion].filter(Boolean).join(' ') || '—';
  }

  appVersion(): string {
    const i = this.device?.deviceInformation;
    if (!i?.appVersion) return '—';
    return i.appBuild ? `${i.appVersion} (${i.appBuild})` : i.appVersion;
  }

  reasonLabel(value: number | null): string {
    if (value === null) return '–';
    return this.reasons.find(r => r.reason === value)?.label ?? '–';
  }

  /**
   * A lifecycle day as German users read it (`21.9.2026`, the form `toLocaleDateString('de-DE')` gives), built from
   * the day's own fields rather than through a `Date`, which would read it in the browser's zone. Anything that is not
   * a `yyyy-MM-dd` day shows as `–`.
   */
  fmt(day: DayKey | null): string {
    if (!isDayKey(day)) return '–';
    const {year, month, day: dayOfMonth} = parseDayKey(day);
    return `${dayOfMonth}.${month}.${year}`;
  }

  /** The value the date input shows: the day as it is, or nothing for a value that is not a `yyyy-MM-dd` day. */
  private toDateInput(day: DayKey): string {
    return isDayKey(day) ? day : '';
  }
}
